import { useState, useMemo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import useAuthStore from '@entities/auth/model/useAuthStore';
import DocumentDetailModal from '@pages/approval-box/components/DocumentDetailModal/DocumentDetailModal';

import {
  useGetPcApprovalDocListQuery,
  useReferenceDocumentStateMutation,
} from '@features/approval/hooks';
import { getFilterFields } from '@features/approval/components/drafter/config/filterFields';
import { tableColumns } from '@features/approval/components/drafter/config/tableColumns';
import useApprovalSection from '@features/approval/hooks/useApprovalSection';
import { useDocFormListSimpleQuery } from '@features/form-selection/hooks';
import useGetTeamQuery from '@features/team/hooks';

import createExcelFile from '@shared/lib/excel';
import { formatDate, getDefaultDateRange } from '@shared/utils/formatDate';
import { useIsMobile } from '@shared/hooks';
import ContentSectionLayout from '@shared/components/organisms/ContentSectionLayout/ContentSectionLayout';
import MainLayout from '@shared/components/organisms/MainLayout/MainLayout';
import TabContentLayout from '@shared/components/organisms/TabContentLayout/TabContentLayout';
import { MobileLayout } from '@shared/components/organisms/mobile';
import Alert from '@shared/components/atoms/Alert';

import styles from '@pages/approval-box/ReferencePage.module.scss';
import {
  MobileReferenceContent,
  MobileDocumentDetail,
} from '@pages/approval-box/components/mobile';

/**
 * ReferencePage 컴포넌트
 * - 결재 문서 중 '참조'로 지정된 문서를 확인하는 페이지입니다.
 * - 주요 기능: 문서 목록 조회, 검색 필터(양식, 기안부서 등), 엑셀 다운로드
 */
function ReferencePage() {
  /**
   * TODO
   * - 탭의 문서 갯수는 신규 결재 문서 발생 시 증가하며 해당 문서를 결재, 반려, 수정요청 하는 시점에 감소
   * - 목록에 완료 항목 추가
   * - 선택 완료 기능 추가 필요
   * - 더블클릭 및 열기
   * - Confirm '완료 상태를 변경하시겠습니까?'
   */

  // 모바일 화면 감지
  const isMobile = useIsMobile();

  /* =======================
   * 기본 조회 기간 (3개월)
   * ======================= */
  const defaultRange = useMemo(() => getDefaultDateRange(), []);

  /* =======================
   * 🔑 searchParams (탭별)
   * ======================= */
  const [searchParams, setSearchParams] = useState({
    date1: defaultRange.start,
    date2: defaultRange.end,
  });

  const [alertState, setAlertState] = useState({
    isOpen: false,
    message: '',
    mode: 'alert',
    onConfirm: null,
  });

  // 상세문서보기 모달 상태
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDocIdx, setSelectedDocIdx] = useState(null);

  const queryClient = useQueryClient();

  /** 문서상세 모달 닫기 시 리스트 재갱신 */
  const closeDetailModal = useCallback(
    async function () {
      setIsModalOpen(false);
      setSelectedDocIdx(null);
      queryClient.invalidateQueries({ queryKey: ['pcApprovalList'] });
      queryClient.invalidateQueries({ queryKey: ['approvalList'] });
      await queryClient.refetchQueries({ queryKey: ['pcApprovalList'] });
      await queryClient.refetchQueries({ queryKey: ['approvalList'] });
    },
    [queryClient],
  );

  function showAlert({ message, mode = 'alert', onConfirm = null }) {
    setAlertState({
      isOpen: true,
      message,
      mode,
      onConfirm,
    });
  }
  /** 로그인된 사용자 회사 코드 조회  */
  const companyCode = useAuthStore((state) => state.companyCode);

  /**
   * 양식 목록 조회
   * - formOptions로 변환하여 필터 Select 박스에 사용
   * - docFormListSimple API 사용 (form_data 제외)
   */
  const { data: docFormList = [] } = useDocFormListSimpleQuery(companyCode);

  /**
   * 필터에서 사용될 문서 양식 선택 옵션 구성
   */
  const formOptions = useMemo(
    function () {
      return [
        { label: '전체', value: '' },
        ...docFormList.map(function (item) {
          return { label: item.title, value: item.doc };
        }),
      ];
    },
    [docFormList],
  );

  /** excel 의 날짜 데이터 format */
  const excelFormatter = useCallback(function (rows) {
    return rows.map(function (r) {
      return {
        ...r,
        date: formatDate(r.date),
        exeState:
          r.exeState === 1 || r.exeState === '1'
            ? '완료'
            : r.exeState === 0 || r.exeState === '0'
              ? '미완료'
              : '알 수 없음',
        corpSealUsed: r.is_corp_seal_used === 1 || r.is_corp_seal_used === '1' ? 'Y' : 'N',
      };
    });
  }, []);

  /**
   * 회사 팀 목록 조회
   * - teamResponse.resultData.teamList 배열을 기반으로 Select 옵션 생성
   */
  const { data: teamResponse } = useGetTeamQuery(companyCode);

  // 팀 선택 필터 옵션 생성
  const teamOption = useMemo(
    function () {
      const teamList = Array.isArray(teamResponse?.resultData?.teamList)
        ? teamResponse.resultData.teamList
        : [];
      return [
        { label: '전체', value: '' },
        ...teamList.map(function (item) {
          return {
            label: item.team,
            value: item.teamCode,
          };
        }),
      ];
    },
    [teamResponse],
  );

  /**
   * 문서 필터 구성 확장 함수
   * - 기본 getFilterFields 결과에서 docType(문서분류) 뒤에 '팀 선택 필드'를 동적으로 삽입합니다.
   * - ReferencePage 특화 필터: 기안 부서(team) 검색 기능 추가
   */
  const getExtendedFilterFields = useCallback(
    function (filterValues, currentFormOptions) {
      // 원본 getFilterFields 호출 (기본 필터 구성 가져오기)
      const baseFields = getFilterFields(filterValues, currentFormOptions);

      // docType 위치(index) 찾기
      const docTypeIndex = baseFields.findIndex(function (f) {
        return f.key === 'docType';
      });

      // 특정 위치(docType 바로 뒤)에 team 필드 삽입하여 새로운 필터 배열 반환
      const extended = [
        ...baseFields.slice(0, docTypeIndex + 1),
        {
          key: 'team',
          type: 'select',
          label: '기안부서',
          value: filterValues.team || null,
          options: teamOption,
        },
        ...baseFields.slice(docTypeIndex + 1),
      ];

      return extended;
    },
    [teamOption],
  );

  // 테이블 컬럼 확장
  const extendedTableColumns = useMemo(
    () => [
      {
        key: 'radio',
        width: '40px',
        className: styles.radioCell,
        sortable: false,
        stopPropagation: true,
        // Note: DOM 직접 접근 방식은 추후 React 상태 기반으로 리팩토링 권장
        headerRender: () => (
          <input
            type="checkbox"
            name="completeRadio"
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              const checked = e.target.checked;
              const rowCheckboxes = document.querySelectorAll('input[name^="row-"]');
              rowCheckboxes.forEach((cb) => (cb.checked = checked));
            }}
          />
        ),
        render: (_, rowItem) => (
          <input type="checkbox" name={`row-${rowItem.idx}`} onClick={(e) => e.stopPropagation()} />
        ),
      },
      {
        key: 'exeState',
        label: '완료여부',
        width: '90px',
        className: styles.colDocumentType,
        render: (_, row) => {
          const value = row.exeState;

          if (value === 1 || value === '1') return '완료';
          if (value === 0 || value === '0') return '미완료';
          return '알 수 없음';
        },
      },
      ...tableColumns,
    ],
    [tableColumns],
  );
  // 2026.02.23 엑셀용 컬럼 생성
  const excelColumns = useMemo(() => {
    return [
      ...extendedTableColumns,
      {
        key: 'corpSealUsed',
        label: '법인인감사용',
        render: (_, row) =>
          row.is_corp_seal_used === 1 || row.is_corp_seal_used === '1' ? 'Y' : 'N',
      },
    ];
  }, [extendedTableColumns]);

  /**
   * 참조함 목록 조회에 필요한 공통 로직 Hook
   * - queryType: 'receive' -> 참조 문서 조회 API 호출을 위한 타입 지정
   * - filterFieldBuilder: 위에서 정의한 확장 필터 함수 전달
   * - useQueryHook: 실제 데이터를 가져오는 React Query Hook
   */
  const receiveSection = useApprovalSection({
    queryType: 'receive',
    searchParams: searchParams,
    setSearchParams: setSearchParams,
    filterFieldBuilder: getExtendedFilterFields,
    tableColumns: extendedTableColumns,
    useQueryHook: useGetPcApprovalDocListQuery,
    formOptions,
    excelFormatter,
    defaultSort: { field: 'date', direction: 'desc' },
  });

  /**
   * 참조 상태 변경 Mutaiotn
   */
  const { mutate: referenceState } = useReferenceDocumentStateMutation({
    onSuccess: () => {
      receiveSection.approvalRefetch();
      //  전체 체크박스 해제
      const rowCheckboxes = document.querySelectorAll('input[name^="row-"]');
      rowCheckboxes.forEach((cb) => {
        cb.checked = false;
      });

      // "전체 선택" 체크박스도 해제
      const headerCheckbox = document.querySelector('input[name="completeRadio"]');
      if (headerCheckbox) {
        headerCheckbox.checked = false;
      }
    },
    onError: (error) => {
      console.error('Mutation 실패:', error.response?.data || error);
    },
  });

  /** 선택 완료 처리 핸들러 */
  function handleComplete() {
    const checkedBoxes = Array.from(document.querySelectorAll('input[name^="row-"]:checked'));
    const selectedDocNumbers = checkedBoxes.map(function (checkbox) {
      return checkbox.getAttribute('name').replace('row-', '');
    });

    if (selectedDocNumbers.length === 0) {
      showAlert({
        message: '선택된 문서가 없습니다.',
        mode: 'alert',
      });
      return;
    }

    // 선택됨 → confirm alert 띄우기
    showAlert({
      message: '완료 상태를 변경하시겠습니까?',
      mode: 'confirm',
      onConfirm: function () {
        referenceState(selectedDocNumbers);
      },
    });
  }

  function closeAlert() {
    setAlertState((prev) => ({
      ...prev,
      isOpen: false,
    }));
  }

  // 행 클릭 핸들러 (코멘트 확인 API 제거 - 모달 내 useDocumentCommentListQuery에서 조회)
  function handleRowClick(item) {
    const docIdx = item.idx;
    if (!docIdx) {
      console.warn('문서 인덱스를 찾을 수 없습니다:', item);
      return;
    }

    setSelectedDocIdx(docIdx);
    setIsModalOpen(true);
  }

  // 모바일 선택 완료
  function handleMobileComplete(selectedIds) {
    if (!selectedIds || selectedIds.length === 0) {
      showAlert({
        message: '선택된 문서가 없습니다.',
        mode: 'alert',
      });
      return;
    }

    showAlert({
      message: '완료 상태를 변경하시겠습니까?',
      mode: 'confirm',
      onConfirm: () => {
        referenceState(selectedIds, {
          onSuccess: () => {
            receiveSection.approvalRefetch(); // 최신 데이터 반영
          },
        });
      },
    });
  }

  // 모바일 레이아웃 렌더링
  if (isMobile) {
    return (
      <MobileLayout title="참조문서">
        <MobileReferenceContent
          receiveSection={receiveSection}
          // data={receiveSection.tableData || []}
          // isLoading={receiveSection.isLoading}
          // isFetching={receiveSection.isFetching}
          onItemClick={handleRowClick}
          formOptions={formOptions}
          teamOptions={teamOption}
          onComplete={handleMobileComplete}
        />
        {/* 모바일 문서 상세 (PC와 다르게 본문 이미지 사용) */}
        <MobileDocumentDetail
          isOpen={isModalOpen}
          onClose={closeDetailModal}
          docIdx={selectedDocIdx}
          userRole="reference"
          tabType="receive"
        />
        {/* Alert 컴포넌트 */}
        <Alert
          isOpen={alertState.isOpen}
          message={alertState.message}
          mode={alertState.mode}
          onConfirm={alertState.onConfirm}
          onClose={closeAlert}
        />
      </MobileLayout>
    );
  }

  // PC 레이아웃 렌더링
  return (
    <MainLayout>
      <TabContentLayout
        tabs={{
          label: '참조함',
          content: (
            <ContentSectionLayout
              {...receiveSection}
              classes={styles}
              showExcelButton={true}
              emptyMessage="현재 참조 문서가 없습니다."
              onExcel={() =>
                createExcelFile({
                  rows: receiveSection.handleExcel(),
                  fileName: '참조문서.xlsx',
                  columns: excelColumns,
                })
              }
              extraContent={
                <button className={styles.completeButton} onClick={handleComplete}>
                  완료 상태 변경
                </button>
              }
              onRowClick={handleRowClick}
            />
          ),
        }}
      />
      {/* 공통 Alert 컴포넌트 */}
      <Alert
        isOpen={alertState.isOpen}
        message={alertState.message}
        mode={alertState.mode}
        onConfirm={alertState.onConfirm}
        onClose={closeAlert}
      />
      {/* 문서 상세 모달 */}
      <DocumentDetailModal
        isOpen={isModalOpen}
        onClose={closeDetailModal}
        docIdx={selectedDocIdx}
        userRole="reference"
        tabType="receive"
      />
    </MainLayout>
  );
}

export default ReferencePage;
